import React from "react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Code, FileCode } from "lucide-react";

export default function ProcessingRuleCodeEditor({
  code,
  onChange,
  language = 'javascript',
  readOnly = false,
  label = "Rule Code",
  rows = 15
}) {
  const getLanguageInfo = (lang) => {
    const langMap = {
      javascript: {
        name: 'JavaScript',
        color: 'bg-yellow-100 text-yellow-800',
        icon: FileCode,
        placeholder: `// JavaScript processing rule
// Input: value (the data to process)
// Output: { isValid: boolean, transformedValue: any, message: string }

function process(value) {
  // Add your logic here
  return {
    isValid: true,
    transformedValue: value,
    message: 'Success'
  };
}

return process(input);`
      },
      python: {
        name: 'Python',
        color: 'bg-blue-100 text-blue-800',
        icon: FileCode,
        placeholder: `# Python processing rule
# Input: value (the data to process)
# Output: { "isValid": bool, "transformedValue": any, "message": str }

def process(value):
    # Add your logic here
    return {
        "isValid": True,
        "transformedValue": value,
        "message": "Success"
    }

return process(input)`
      },
      sql: {
        name: 'SQL',
        color: 'bg-cyan-100 text-cyan-800',
        icon: Code,
        placeholder: `-- SQL processing rule
-- Use {{input}} to reference the input value

SELECT
  CASE
    WHEN {{input}} IS NOT NULL THEN 'valid'
    ELSE 'invalid'
  END as validation_result,
  {{input}} as transformed_value`
      },
      tiva_endpoint: {
        name: 'Tiva Endpoint',
        color: 'bg-indigo-100 text-indigo-800',
        icon: Code,
        placeholder: `// Tiva endpoint reference
// The input will be passed as payload to the endpoint

{
  "endpoint": "your.endpoint.name",
  "config": {
    "timeout": 30000
  }
}`
      }
    };
    return langMap[lang] || langMap.javascript;
  };

  const langInfo = getLanguageInfo(language);
  const LangIcon = langInfo.icon;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="code-editor" className="flex items-center gap-2">
          <LangIcon className="w-4 h-4 text-slate-500" />
          {label}
        </Label>
        <Badge className={`${langInfo.color} text-xs`}>
          {langInfo.name}
        </Badge>
      </div>
      <Card className="border-slate-200 overflow-hidden">
        <CardContent className="p-0">
          <div className="bg-slate-800 text-slate-300 px-3 py-1.5 text-xs font-mono flex items-center justify-between">
            <span>code.{language === 'python' ? 'py' : language === 'javascript' ? 'js' : language === 'sql' ? 'sql' : 'json'}</span>
            {readOnly && <span className="text-slate-500">Read only</span>}
          </div>
          <Textarea
            id="code-editor"
            value={code}
            onChange={(e) => onChange && onChange(e.target.value)}
            placeholder={langInfo.placeholder}
            className="font-mono text-sm border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-slate-900 text-slate-100 resize-none"
            rows={rows}
            readOnly={readOnly}
            style={{
              lineHeight: '1.5',
              tabSize: 2
            }}
          />
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">
        {language === 'javascript' && 'Use standard JavaScript. The input variable contains the value to process.'}
        {language === 'python' && 'Use standard Python 3. The input variable contains the value to process.'}
        {language === 'sql' && 'Use {{input}} placeholder to reference the input value.'}
        {language === 'tiva_endpoint' && 'Specify the endpoint configuration. Input will be passed as payload.'}
      </p>
    </div>
  );
}
